import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import emailService, { type EmailLog, type EmailLogResponse } from '../../services/emailService';

export default function EmailReceipts() {
  const [logsData, setLogsData] = useState<EmailLogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'sent' | 'failed' | 'pending'>('all');

  useEffect(() => {
    loadReceipts();
  }, []);

  const loadReceipts = async () => {
    setLoading(true);
    try {
      // Récupérer les logs de toutes les campagnes
      const logs = await emailService.getAllLogs();

      // Si aucun log, créer structure vide
      if (!logs || logs.length === 0) {
        setLogsData({
          campaign: { id: '', subject: 'Aucune campagne' },
          statistics: { total: 0, sent: 0, failed: 0, pending: 0 },
          logs: [],
        });
        return;
      }

      // Grouper par campagne pour affichage
      const campaignMap: Record<string, { logs: EmailLog[]; stats: Record<string, number> }> = {};

      logs.forEach((log) => {
        const campaignId = String(log.campaign);
        if (!campaignMap[campaignId]) {
          campaignMap[campaignId] = {
            logs: [],
            stats: { total: 0, sent: 0, failed: 0, pending: 0 },
          };
        }

        campaignMap[campaignId].logs.push(log);
        campaignMap[campaignId].stats.total++;
        if (log.status === 'sent') campaignMap[campaignId].stats.sent++;
        if (log.status === 'failed') campaignMap[campaignId].stats.failed++;
        if (log.status === 'pending') campaignMap[campaignId].stats.pending++;
      });

      // Prendre la première campagne
      const firstCampaignId = Object.keys(campaignMap)[0];
      const firstCampaignData = campaignMap[firstCampaignId];

      const firstCampaign: EmailLogResponse = {
        campaign: {
          id: firstCampaignId,
          subject:
            firstCampaignData.logs[0]?.campaign_subject || 'Campagne',
        },
        statistics: {
          total: firstCampaignData.stats.total,
          sent: firstCampaignData.stats.sent,
          failed: firstCampaignData.stats.failed,
          pending: firstCampaignData.stats.pending,
        },
        logs: firstCampaignData.logs,
      };

      setLogsData(firstCampaign);
    } catch (error) {
      console.error('Erreur chargement accusés:', error);
      toast.error('Erreur lors du chargement des accusés de réception');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      sent: 'badge-success',
      failed: 'badge-error',
      pending: 'badge-warning',
    };

    const labels: Record<string, string> = {
      sent: 'Envoyé',
      failed: 'Échoué',
      pending: 'En attente',
    };

    const icons: Record<string, string> = {
      sent: '✓',
      failed: '✗',
      pending: '⏳',
    };

    return (
      <span className={`badge ${styles[status] || 'badge-ghost'} gap-1`}>
        <span>{icons[status] || '•'}</span>
        {labels[status] || status}
      </span>
    );
  };

  const filteredLogs = logsData?.logs.filter((log) => {
    if (filter === 'all') return true;
    return log.status === filter;
  }) || [];

  const deleteLog = async (id: string) => {
    if (!window.confirm('Supprimer cet accusé ?')) return;
    try {
      await emailService.deleteEmailLog(id);
      setLogsData((prev) => {
        if (!prev) return prev;
        const updatedLogs = prev.logs.filter((l) => l.id !== id);
        const stats = {
          total: prev.statistics.total - 1,
          sent: prev.statistics.sent - (prev.logs.find(l => l.id === id && l.status === 'sent') ? 1 : 0),
          failed: prev.statistics.failed - (prev.logs.find(l => l.id === id && l.status === 'failed') ? 1 : 0),
          pending: prev.statistics.pending - (prev.logs.find(l => l.id === id && l.status === 'pending') ? 1 : 0),
        };
        return {
          ...prev,
          logs: updatedLogs,
          statistics: stats,
        };
      });
      toast.success('Accusé supprimé');
    } catch (error) {
      console.error('Erreur suppression accusé:', error);
      toast.error('Suppression impossible');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <span className="loading loading-spinner loading-lg text-warning"></span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques */}
      {logsData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat bg-white shadow-md rounded-lg">
            <div className="stat-title">Total</div>
            <div className="stat-value text-yellow-500">{logsData.statistics.total}</div>
          </div>
          <div className="stat bg-white shadow-md rounded-lg">
            <div className="stat-title">Envoyés</div>
            <div className="stat-value text-green-500">{logsData.statistics.sent}</div>
          </div>
          <div className="stat bg-white shadow-md rounded-lg">
            <div className="stat-title">Échoués</div>
            <div className="stat-value text-red-500">{logsData.statistics.failed}</div>
          </div>
          <div className="stat bg-white shadow-md rounded-lg">
            <div className="stat-title">En attente</div>
            <div className="stat-value text-orange-500">{logsData.statistics.pending}</div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="card bg-white shadow-xl">
        <div className="card-body p-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`btn btn-sm ${
                filter === 'all' ? 'btn-warning' : 'btn-outline btn-warning'
              }`}
            >
              Tous ({logsData?.logs.length || 0})
            </button>
            <button
              onClick={() => setFilter('sent')}
              className={`btn btn-sm ${
                filter === 'sent' ? 'btn-success' : 'btn-outline btn-success'
              }`}
            >
              Envoyés ({logsData?.statistics.sent || 0})
            </button>
            <button
              onClick={() => setFilter('failed')}
              className={`btn btn-sm ${
                filter === 'failed' ? 'btn-error' : 'btn-outline btn-error'
              }`}
            >
              Échoués ({logsData?.statistics.failed || 0})
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`btn btn-sm ${
                filter === 'pending' ? 'btn-warning' : 'btn-outline btn-warning'
              }`}
            >
              En attente ({logsData?.statistics.pending || 0})
            </button>
          </div>
        </div>
      </div>

      {/* Liste des accusés */}
      <div className="card bg-white shadow-xl">
        <div className="card-body">
          <h2 className="card-title text-warning mb-4">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Accusés de réception
          </h2>

          {filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <svg
                className="w-16 h-16 mx-auto mb-3 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p>Aucun accusé de réception</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Statut</th>
                    <th>Employé</th>
                    <th>Email</th>
                    <th>Créé le</th>
                    <th>Envoyé le</th>
                    <th>Erreur</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log) => (
                    <tr
                      key={log.id}
                      className={log.status === 'failed' ? 'bg-red-50' : ''}
                    >
                      <td>{getStatusBadge(log.status)}</td>
                      <td className="font-medium">{log.employee_name}</td>
                      <td className="text-sm text-gray-600">{log.employee_email}</td>
                      <td className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="text-xs text-gray-500">
                        {log.sent_at
                          ? new Date(log.sent_at).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '-'}
                      </td>
                      <td
                        className="text-xs text-red-600 max-w-xs truncate"
                        title={log.error_message}
                      >
                        {log.error_message || '-'}
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-xs text-error hover:bg-red-50"
                          onClick={() => deleteLog(log.id)}
                          title="Supprimer l'accusé"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
